import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {ApexOptions} from 'apexcharts'
import {ApexChart, useChartTokens, useSegmentNavigation} from '@/components/ui/apex-chart'
import {SectionCard} from '@/components/section-card'
import {Tag} from '@/components/ui/tag'
import {HelpIcon} from '@/components/help-icon'
import type {NivelResumen} from '@/hooks/bateria/use-bateria-informe'
import {NIVEL_TONE, NIVELES, prettifyTaxonomia, severidadRankNivel} from '@/lib/bateria'

const NIVEL_TAG_TONE: Record<string, 'green' | 'amber' | 'orange' | 'red'> = NIVEL_TONE

function ordenarPeorPrimero(niveles: NivelResumen[]): NivelResumen[] {
  return [...niveles].sort((a, b) => severidadRankNivel(a.nivel) - severidadRankNivel(b.nivel))
}

/** Bloque 4 (§P7). Cada barra lleva el color de su nivel: los cortes del baremo son propios de cada
 * dominio y no hay un umbral global que dibujar. */
export function DominiosBarras({dominios}: {dominios: NivelResumen[]}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const ordenados = useMemo(() => ordenarPeorPrimero(dominios), [dominios])
  const colorPorNivel: Record<string, string> = useMemo(
    () => ({
      sin_riesgo: tokens.primary,
      bajo: tokens['chart-actuar'],
      medio: tokens['tone-amber-fg'],
      alto: tokens['tone-orange-fg'],
      muy_alto: tokens['tone-red-fg'],
    }),
    [tokens]
  )
  const options = useMemo<ApexOptions>(
    () => ({
      chart: {type: 'bar'},
      colors: ordenados.map((d) => colorPorNivel[d.nivel ?? ''] ?? tokens['muted-foreground']),
      plotOptions: {
        bar: {horizontal: true, distributed: true, borderRadius: 3, borderRadiusApplication: 'end', barHeight: '58%'},
      },
      xaxis: {categories: ordenados.map((d) => prettifyTaxonomia(d.clave)), max: 100},
      legend: {show: false},
      tooltip: {
        y: {
          formatter: (value: number, opts?: {dataPointIndex: number}) => {
            const d = ordenados[opts?.dataPointIndex ?? 0]
            return t('admin.bateria.informe.dominios.tooltip', {
              value,
              nivel: d.nivel ? t(`admin.bateria.nivel.${d.nivel}`) : t('admin.bateria.nivel.sinDato'),
            })
          },
        },
      },
    }),
    [ordenados, colorPorNivel, tokens, t]
  )
  const series = useMemo(
    () => [
      {
        name: t('admin.bateria.informe.dominios.serie'),
        data: ordenados.map((d) => Math.round(d.promedio_transformado ?? 0)),
      },
    ],
    [ordenados, t]
  )

  if (ordenados.length === 0) return null

  return (
    <SectionCard
      title={t('admin.bateria.informe.dominios.title')}
      subtitle={t('admin.bateria.informe.dominios.subtitle')}
      testId="informe-dominios">
      <ApexChart
        type="bar"
        height={Math.max(160, ordenados.length * 42)}
        options={options}
        series={series}
        testId="informe-dominios-chart"
      />
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5" aria-hidden>
        {NIVELES.map((n) => (
          <li key={n} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="inline-block size-2.5 rounded-[2px]" style={{backgroundColor: colorPorNivel[n]}} />
            {t(`admin.bateria.nivel.${n}`)}
          </li>
        ))}
      </ul>
    </SectionCard>
  )
}

/** Bloque 5 (§P7). Un clic en un segmento lleva a su fila en la tabla de detalle. */
export function DimensionesApiladas({dimensiones}: {dimensiones: NivelResumen[]}) {
  const {t} = useTranslation()
  const tokens = useChartTokens()
  const ordenadas = useMemo(() => ordenarPeorPrimero(dimensiones), [dimensiones])
  const colorPorNivel: Record<string, string> = useMemo(
    () => ({
      sin_riesgo: tokens.primary,
      bajo: tokens['chart-actuar'],
      medio: tokens['tone-amber-fg'],
      alto: tokens['tone-orange-fg'],
      muy_alto: tokens['tone-red-fg'],
    }),
    [tokens]
  )
  const claves = useMemo(() => ordenadas.map((d) => d.clave), [ordenadas])
  const irADetalle = (clave: string) => {
    const fila = document.getElementById(`informe-dimension-detalle-${clave}`)
    if (!fila) return
    fila.scrollIntoView({behavior: 'smooth', block: 'center'})
    fila.focus({preventScroll: true})
  }
  const dataPointSelection = useSegmentNavigation(claves, irADetalle)

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {type: 'bar', stacked: true, stackType: '100%', events: {dataPointSelection}},
      colors: NIVELES.map((n) => colorPorNivel[n]),
      plotOptions: {bar: {horizontal: true, borderRadius: 2, barHeight: '62%'}},
      xaxis: {categories: ordenadas.map((d) => prettifyTaxonomia(d.clave))},
      legend: {show: false},
      tooltip: {
        shared: true,
        intersect: false,
        y: {formatter: (value: number) => t('admin.bateria.informe.reparto.tooltip', {count: value})},
      },
    }),
    [ordenadas, colorPorNivel, dataPointSelection, t]
  )
  const series = useMemo(
    () =>
      NIVELES.map((n) => ({name: t(`admin.bateria.nivel.${n}`), data: ordenadas.map((d) => d.distribucion[n] ?? 0)})),
    [ordenadas, t]
  )

  if (ordenadas.length === 0) return null

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-1.5">
          {t('admin.bateria.informe.dimensiones.title')}
          {/* T9: el subtítulo es espejo del DOCX y no se toca; la ayuda del clic va en la «i». */}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.informe.dimensiones.title')})}
            testId="informe-dimensiones-clic-tip">
            {t('admin.bateria.informe.dimensiones.clicTip')}
          </HelpIcon>
        </span>
      }
      subtitle={t('admin.bateria.informe.dimensiones.subtitle')}
      testId="informe-dimensiones">
      <ApexChart
        type="bar"
        height={Math.max(220, ordenadas.length * 34)}
        options={options}
        series={series}
        clickable
        testId="informe-dimensiones-chart"
      />

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-border text-[11px] font-medium tracking-wider text-muted-foreground uppercase">
              <th className="py-2 pr-3">{t('admin.bateria.informe.dimensiones.colDimension')}</th>
              <th className="py-2 pr-3">{t('admin.bateria.informe.dimensiones.colNivel')}</th>
              <th className="py-2 pr-3">{t('admin.bateria.informe.dimensiones.colPromedio')}</th>
              <th className="py-2 pr-3">{t('admin.bateria.informe.dimensiones.colN')}</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.map((d) => (
              <tr
                key={d.clave}
                id={`informe-dimension-detalle-${d.clave}`}
                tabIndex={-1}
                data-testid={`informe-dimension-detalle-${d.clave}`}
                className="surface-divider border-b outline-none last:border-0 focus-visible:bg-accent/40">
                <td className="py-2 pr-3 text-foreground">{d.label}</td>
                <td className="py-2 pr-3">
                  {d.nivel ? (
                    <Tag tone={NIVEL_TAG_TONE[d.nivel] ?? 'neutral'}>{t(`admin.bateria.nivel.${d.nivel}`)}</Tag>
                  ) : (
                    <Tag tone="neutral">{t('admin.bateria.nivel.sinDato')}</Tag>
                  )}
                </td>
                <td className="py-2 pr-3 font-mono text-xs">
                  {d.promedio_transformado === null ? '—' : d.promedio_transformado.toFixed(1)}
                </td>
                <td className="py-2 pr-3 font-mono text-xs">{d.n_validos}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}
